import React, { useState, useEffect, useRef, RefObject } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as styles from './NotesDialog.module.scss';
import { PartyAvatar } from 'components/shared';
import {
  Badge,
  Button,
  FormControl,
  FormLabel,
  Icon,
  IconList,
  Message,
  Strong,
  Text,
  TextArea,
  VisuallyHidden,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import type { Note } from 'pages/StudyList/StudyList';
import { updateNote, deleteNote } from 'query/study/Notes';
import { Reviewer } from 'types';
import { dateTimeString, truncateAtWordBoundary } from 'pages/StudyList/util';

const reviewerName = (note: Note) => {
  return `${note.reviewer_first_name} ${note.reviewer_last_name}`;
};

const displayName = (category: string) => {
  switch (category) {
    case 'Screen':
      return 'Title & Abstract';
    case 'Select':
      return 'Full Text Review';
    case 'Included':
      return 'Extraction';
    case 'identification':
      return 'Extraction (Identification)';
    case 'study_design':
      return 'Extraction (Methods)';
    case 'population':
      return 'Extraction (Population)';
    case 'interventions':
      return 'Extraction (Interventions)';
    case 'outcomes':
      return 'Extraction (Outcomes)';
    case 'results data':
      return 'Extraction (Results Data)';
    default:
      return category;
  }
};

const badgeType = (category: string) => {
  switch (category) {
    case 'Screen':
      return 'primary';
    case 'Select':
      return 'warning';
    default:
      return 'success';
  }
};

export type ClaimFocusProps = {
  id?: number;
  idAfter?: number;
};

type Props = {
  note: Note;
  currentReviewer: Reviewer | undefined;
  onChange: () => Promise<void>;
  claimFocus: ({ id, idAfter }: ClaimFocusProps) => void;
  isFocused: boolean;
};

const Note = ({
  note,
  currentReviewer,
  onChange,
  claimFocus,
  isFocused,
}: Props) => {
  const editButton = useRef<HTMLButtonElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const textArea = useRef<HTMLTextAreaElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isCheckDeleting, setIsCheckDeleting] = useState(false);
  const [value, setValue] = useState(note.note);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const [
    focusedElement,
    setFocusedElement,
  ] = useState<RefObject<HTMLElement> | null>(isFocused ? editButton : null);
  const isNoteOwner = currentReviewer?.id === note.reviewer_id;

  const {
    mutate: update,
    isLoading: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: (text: string) => updateNote(text, note.id),
    onSuccess: async () => {
      await onChange();
      setIsEditing(false);
      claimFocus({ id: note.id });
    },
  });

  const {
    mutate: confirmDelete,
    isLoading: isDeleting,
    error: deleteError,
  } = useMutation({
    mutationFn: () => deleteNote(note.id),
    onSuccess: async () => {
      await onChange();
      setIsCheckDeleting(false);
      setIsEditing(false);
      claimFocus({ idAfter: note.id });
    },
  });

  useEffect(() => {
    if (isFocused && !isEditing) {
      editButton.current?.focus();
      setFocusedElement(editButton);
      return;
    }

    focusedElement?.current?.focus();
  }, [focusedElement?.current, isFocused, isEditing]);

  const switchToEditing = () => {
    setValidationMessage(null);
    setIsEditing(true);
    setFocusedElement(textArea);
  };

  const onClickSave = () => {
    if (value.length === 0) {
      setValidationMessage('Note cannot be empty');
      return;
    }

    update(value);
  };

  const onClickCancel = () => {
    setValidationMessage(null);
    setIsEditing(false);
    setFocusedElement(editButton);
  };

  const onClickDelete = () => {
    setIsCheckDeleting(true);
  };

  const onClickConfirmDelete = () => {
    confirmDelete();
  };

  const onClickCancelDelete = () => {
    setIsCheckDeleting(false);
    setFocusedElement(deleteButton);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
    if (event.target.value.trim().length > 0) {
      setValidationMessage(null);
    }
  };

  return (
    <Stack component="li" spacing={2} className={styles.Note}>
      <FormLabel htmlFor={`note-text-${note.id}`}>
        <Inline alignX="left" spacing={2}>
          <PartyAvatar
            size="sm"
            name={reviewerName(note)}
            variant={isNoteOwner ? 'user' : 'other_user'}
          />
          <Strong aria-hidden="true">{reviewerName(note)}</Strong>
          <Text variant="weak" size="sm" component="span">
            {dateTimeString(note.created_at)}
          </Text>
          <Badge type={badgeType(note.category)}>
            {displayName(note.category)}
          </Badge>
          <VisuallyHidden>- Edit note</VisuallyHidden>
        </Inline>
      </FormLabel>
      {!isEditing && (
        <>
          <Text data-testclass="note">{note.note}</Text>
          {isNoteOwner && (
            <Button
              variant="outline"
              className={styles.EditNoteButton}
              size="sm"
              onClick={switchToEditing}
              ref={editButton}
              aria-label={`Edit note: ${truncateAtWordBoundary(note.note)}`}
            >
              <Icon icon={IconList.light.faPenToSquare} aria-hidden="true" />
              Edit note
            </Button>
          )}
        </>
      )}
      {isEditing && (
        <>
          <FormControl error={validationMessage}>
            <TextArea
              id={`note-text-${note.id}`}
              onChange={onInputChange}
              value={value}
              ref={textArea}
            />
          </FormControl>
          {(updateError || deleteError) && (
            <Message type="danger" variant="prominent" role="alert">
              <Text>
                Error saving changes. Please try again or refresh the page.
              </Text>
            </Message>
          )}
          {!isCheckDeleting && (
            <Inline alignX="left" spacing={2}>
              <Button
                variant="solid"
                type="primary"
                size="sm"
                onClick={onClickSave}
                disabled={isUpdating}
                isLoading={isUpdating}
              >
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onClickCancel}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                type="danger"
                className={styles.DeleteNoteButton}
                size="sm"
                onClick={onClickDelete}
                disabled={isUpdating}
                ref={deleteButton}
              >
                Delete
              </Button>
            </Inline>
          )}
          {isCheckDeleting && (
            <Inline alignX="right" spacing={2}>
              <Button
                variant="solid"
                type="danger"
                size="sm"
                onClick={onClickConfirmDelete}
                disabled={isDeleting}
                isLoading={isDeleting}
              >
                Confirm delete
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onClickCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </Inline>
          )}
        </>
      )}
    </Stack>
  );
};

export default Note;
